// @ts-nocheck
// Agent Portal (/cti2) — full "My Groups" dashboard section, rendered
// inline in AgentDashboardView the same way Leads/Voicemails/etc. are,
// instead of the compact Groups popover in AgentContextStrip.tsx. The
// Topbar's "Groups" chip now just scrolls down to this section (see
// Topbar.tsx's handleGroupsSectionClick) rather than opening a dropdown —
// per request ("jaise baki tab pr click krne pr niche container m show
// krte hai, vaise Groups ko bhi"). Data-fetching/toggle logic duplicated
// from AgentContextStrip.tsx rather than shared, matching this codebase's
// convention for small self-contained logic.
import { useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { clearApiCallShared, getApiCallShared } from '@/service/sharedGet';
import { API_AGENT_SCHEDULE, CALL_GROUP_TOGGLE_AGENT } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { useModuleOps } from '../hooks/useModuleOps';
import { useSectionRefresh } from './DashboardSection';
import { useDashboardRefresh } from '../useDashboardRefresh';
import { HAIRLINE } from '../team/teamUi';
import { Switch } from '@/components/ui/switch';

function isInSchedule(schedule) {
  if (!schedule?.start_time || !schedule?.end_time) return null;
  const now = new Date();
  const days = Array.isArray(schedule.week_days) ? schedule.week_days : [];
  if (days.length && !days.includes(now.toLocaleString('en-US', { weekday: 'long' }))) return false;
  const [sh, sm] = String(schedule.start_time).split(':').map(Number);
  const [eh, em] = String(schedule.end_time).split(':').map(Number);
  const startMin = sh * 60 + (sm || 0);
  const endMin = eh * 60 + (em || 0);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return startMin > endMin
    ? nowMin >= startMin || nowMin < endMin
    : nowMin >= startMin && nowMin < endMin;
}

function StatusDot({ active }) {
  return (
    <span className={`inline-block h-1.5 w-1.5 rounded-full ${active ? 'bg-success' : 'bg-muted-foreground/40'}`} />
  );
}

function InSchedulePill({ schedule }) {
  const inSchedule = isInSchedule(schedule);
  if (inSchedule === null) return null;
  return (
    <span
      className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
        inSchedule ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
      }`}
    >
      {inSchedule ? 'In' : 'Out'}
    </span>
  );
}

function ScheduleBlock({ icon, label, schedule }) {
  const inSchedule = isInSchedule(schedule);
  return (
    <div className={`rounded-lg border ${HAIRLINE} bg-muted/30 px-2.5 py-2`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {icon} {label}
        </span>
        {inSchedule !== null && (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              inSchedule ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'
            }`}
          >
            {inSchedule ? 'In' : 'Out'}
          </span>
        )}
      </div>
      {schedule?.start_time && schedule?.end_time ? (
        <>
          <div className="font-mono text-xs font-semibold text-foreground">
            {schedule.start_time}–{schedule.end_time}
          </div>
          {Array.isArray(schedule.week_days) && schedule.week_days.length > 0 && (
            <div className="mt-0.5 text-[10.5px] text-muted-foreground">{schedule.week_days.join(', ')}</div>
          )}
        </>
      ) : (
        <div className="text-[10.5px] text-muted-foreground">Not assigned</div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex shrink-0 items-center gap-1 whitespace-nowrap">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}

export default function AgentGroupsSection({ agentId, userData }) {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = usePersistedState('cti2/AgentGroupsSection:search', '');
  const [togglingGroupId, setTogglingGroupId] = useState(null);

  const moduleOps = useModuleOps();
  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const canReadCallGroup = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'read');
  const canEditCallGroup = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'edit');

  useEffect(() => {
    if (!agentId) return;
    let mounted = true;
    setLoading(true);
    // shared: the Topbar strip and My Groups both load this on mount
    getApiCallShared(`${API_AGENT_SCHEDULE}/${agentId}`)
      .then((resp: any) => {
        if (!mounted) return;
        setGroups(Array.isArray(resp?.groups) ? resp.groups : []);
      })
      .catch(() => {
        if (mounted) setGroups([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [agentId]);

  useDashboardRefresh('groups', () => {
    if (!agentId) return undefined;
    return getApiCallShared(`${API_AGENT_SCHEDULE}/${agentId}`)
      .then((resp: any) => setGroups(Array.isArray(resp?.groups) ? resp.groups : []))
      .catch(() => {});
  });

  useSectionRefresh(() => {
    if (!agentId) return undefined;
    clearApiCallShared();
    return getApiCallShared(`${API_AGENT_SCHEDULE}/${agentId}`)
      .then((resp: any) => setGroups(Array.isArray(resp?.groups) ? resp.groups : []))
      .catch(() => {});
  });

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => String(g.group_name || '').toLowerCase().includes(q));
  }, [groups, search]);

  const agentRecordId = userData?.agentData?.id;

  const handleToggleGroup = async (grp, nextActive) => {
    const groupId = grp?.group_id;
    if (!groupId || !agentRecordId || togglingGroupId) return;
    setTogglingGroupId(groupId);
    try {
      await postApiCall(`${CALL_GROUP_TOGGLE_AGENT}/${groupId}`, {
        agent_id: agentRecordId,
        is_active: nextActive,
      });
      setGroups((prev) => prev.map((g) => (g.group_id === groupId ? { ...g, is_active: nextActive } : g)));
      clearApiCallShared();
      toast.success(`Call group turned ${nextActive ? 'on' : 'off'}`);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to toggle call group.');
    } finally {
      setTogglingGroupId(null);
    }
  };

  if (!canReadCallGroup) {
    return <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">ACL permission missing for Call Groups.</div>;
  }

  return (
    <div className="space-y-2.5">
      {groups.length > 4 && (
        <div className={`flex items-center gap-1.5 rounded-lg border ${HAIRLINE} bg-muted/20 px-2.5 py-1.5`}>
          <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups…"
            className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
      {filteredGroups.length === 0 ? (
        <div className="px-2 py-6 text-center text-xs text-muted-foreground">
          {loading ? 'Loading…' : search ? 'No groups match' : 'No groups assigned'}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {filteredGroups.map((g) => (
            <div key={g.group_id} className={`rounded-xl border ${HAIRLINE} bg-card p-3 shadow-sm`}>
              {/* Name, Group schedule, and Your schedule all in the one
                * row, per request ("2-vk_cg_4 Groups you taki same 1 hi
                * row m show ho") — the grid above switched to a single
                * stacked column so each card has the width for this. */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <StatusDot active={g.is_active !== false} />
                  <span className="truncate text-sm font-semibold text-foreground">
                    {g.group_name || `Group #${g.group_id}`}
                  </span>
                  <InSchedulePill schedule={g.schedule || g.group_schedule} />
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <ScheduleBlock icon="🧩" label="Group" schedule={g.group_schedule} />
                  <ScheduleBlock icon="👤" label="You" schedule={g.schedule} />
                </div>
                {canEditCallGroup ? (
                  togglingGroupId === g.group_id ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    <Switch
                      checked={g.is_active !== false}
                      onCheckedChange={(checked) => handleToggleGroup(g, checked)}
                      className="shrink-0 scale-75"
                    />
                  )
                ) : null}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <DetailRow label="Owner" value={g.group_owner} />
                <DetailRow label="Priority" value={g.priority} />
                <DetailRow label="Status" value={g.is_active !== false ? 'Active' : 'Inactive'} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
